import { useState } from "react";
import pLimit from 'p-limit';
import ExcelJS from 'exceljs';
import { Buffer } from 'buffer';
import {
    CAPACITY_SIZE,
    COATING_METHOD,
    COLOR,
    ITEM_MATERIAL,
    ITEM_NAME,
    LITRES_PER_MINUTE,
    OUTLET_LENGTH,
    PAN_HEIGHT,
    PRODUCT_TYPE,
    RANGE
} from '@/helpers/constants/attributes';
import { CurrencyHelpers } from '@/helpers/currencyHelpers';
import Spinner from "@/components/organisms/spinner";
import getImageBase64 from "@/helpers/utils/generatePdfFromHtml";
import { downloadExcelSheet, exportBlobAsFile } from "@/helpers/utils/downloadExcelSheet";
import { EXPORT_TO_EXCEL, EXPORT_TO_CSV } from "@/helpers/constants/events";
import useProjectBuilder from "@/lib/hooks/useProjectBuilder";
import { checkIsBuilderProduct } from "@/helpers/builder-product-helper";


const ExportToExcelAndCsv: React.FC<any> = ({ projectFormData, selectDataForPublishedProducts }: any) => {
    const { projectName, roomGroups } = projectFormData;
    const allProducts = roomGroups?.flatMap((room: any) => room?.items?.filter((item: any) => item.isProductAvailable));
    const limit = pLimit(5);
    const { updateProjectStatus, getProjectCategories } = useProjectBuilder();
    const [errorMessage, setErrorMessage] = useState('');

    const [loading, setLoading] = useState<boolean>(false);

    const downloadExcelFrontend = async (data: any[], filename = 'export') => {
        if (!data || data.length === 0) {
            console.error('No data provided for Excel export.');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        const columns = Object.keys(data[0] || {}).map((key) => ({
            header: key,
            key: key,
            width: 35,
        }));
        worksheet.columns = columns;

        worksheet.getRow(1).height = 30;
        worksheet.getRow(1).eachCell((cell: any) => {
            cell.font = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFDDDDDD' },
            };
        });

        const imageColIndex = columns.findIndex((c) => c.key === 'Image');

        for (let i = 0; i < data.length; i++) {
            const rowIndex = i + 2;
            const rowData = data[i];
            worksheet.addRow(rowData);
            worksheet.getRow(rowIndex).height = 150;

            columns.forEach((col, colIndex) => {
                if (col.key !== 'Image') {
                    worksheet.getRow(rowIndex).getCell(colIndex + 1).value = rowData[col.key];
                    worksheet.getRow(rowIndex).getCell(colIndex + 1).alignment = {
                        wrapText: true,
                        vertical: 'top',
                    };
                }
            });

            if (rowData.Image && imageColIndex !== -1) {
                try {
                    const buffer = Buffer.from(rowData.Image, 'base64') as unknown as ExcelJS.Buffer;

                    const imageId = workbook.addImage({
                        buffer,
                        extension: 'jpeg',
                    });

                    worksheet.addImage(imageId, {
                        tl: { col: imageColIndex + 0.1, row: i + 1 + 0.1 },
                        ext: { width: 200, height: 170 },
                        editAs: 'oneCell',
                    });

                    worksheet.getRow(rowIndex).getCell(imageColIndex + 1).value = '';
                } catch (err) {
                    console.warn(`Image load failed for row ${i + 1}:`, err);
                }
            }
        }

        downloadExcelSheet(workbook, filename);
    };

    const handleDownloadExcel = async () => {
        const filename = projectName.replace(/\s+/g, '_');
        try {
            setLoading(true);
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            // @ts-ignore
            window.dataLayer.push({
                event: EXPORT_TO_EXCEL,
                'project name': projectName,
            });            
            const pathSegments = window.location.pathname.split('/').filter(Boolean);
            const locale = pathSegments.length > 0 ? pathSegments[0] : 'au';
            const enhancedData = await Promise.all(
                allProducts.map(async (item: any) => limit(async () => {
                    const base64 = await getImageBase64(locale, item?.variant?.images?.[0] || `${window.location.origin}/images/no-image-placeholder/product-thumbnail.jpg`);
                    const attributes = formatAttributes(item);
                    return {
                        Room: item?.roomName,
                        'Product Name': item?.name,
                        ...(base64 ? { Image: base64 } : {}),
                        'Product Code': item?.variant?.attributes?.['item-code'],
                        'Colour Variant': item?.variant?.attributes?.colour,
                        'Specification/Details': attributes.slice(0, 8).map((a: any) => `${a.name}: ${a.value}`).join('\n'),
                        Notes: item?.roomNotes ?? '',
                        Quantity: item?.quantity?.toString(),
                        'Price': checkIsBuilderProduct(item, selectDataForPublishedProducts, locale)
                          ? ''
                          : CurrencyHelpers.formatForCurrency(
                              item.quantity * item?.variant?.price?.centAmount,
                              'en',
                              'USD',
                              2,
                          ).replace('US', ''),
                    };
                })),
            );
            await downloadExcelFrontend(enhancedData, filename);

        } catch (error) {
            console.error('Download error:', error);
            setErrorMessage('Failed to download the file. Please try again later.');
        } finally {
            setLoading(false);
            setTimeout(() => setErrorMessage(''), 5000);

        }
    };

    const fetchCategory = async () => {
        const payload = {
            shoppingListId: projectFormData?.shoppingListId,
        };
        const response = await getProjectCategories(payload) as unknown as any[];
        return response;
    };
    const downloadCSVFrontend = (data: any[], filename = 'export') => {
        if (!data || data.length === 0) {
            console.error('No data provided for CSV export.');
            return;
        }

        const keys = Object.keys(data[0]);

        const csvContent = [
            keys.join(','),
            ...data.map(row =>
                keys.map(key => {
                    const cell = row[key];


                    if (typeof cell === 'string' && cell.startsWith('="') && cell.endsWith('"')) {
                        return cell;
                    }

                    const escaped = String(cell ?? '').replace(/"/g, '""');
                    return `"${escaped}"`;
                }).join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        const now = new Date();
        const formattedDate = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
        const fullFilename = `${filename}_${formattedDate}.csv`;

        exportBlobAsFile(blob, fullFilename);
    };

    const handleDownloadCSV = async () => {
    const filename = projectName.replace(/\s+/g, '_');

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const locale = pathSegments.length > 0 ? pathSegments[0] : 'au';

        const escapeCSVValue = (value: any) => {
            if (value == null) return '';
            return String(value).replace(/[\r\n]+/g, ' ').trim();
        };

        try {
            setLoading(true);
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            // @ts-ignore
            window.dataLayer.push({
                event: EXPORT_TO_CSV,
                'project name': projectName,
            });
            const response = await fetchCategory();
            const enhancedData = await Promise.all(
                allProducts.map(async (item: any) => limit(async () => {
                    const matchedCategory = response.find((cat: any) => cat.id === item.productId);
                    return {
                        Room: item?.roomName,
                        'Product Name': escapeCSVValue(item?.name),
                        'Brand': item?.variant?.attributes?.brand,
                        'Category': matchedCategory?.categoryName || 'N/A',
                        'SKU Code': `\t${item?.variant?.attributes?.['item-code']}`,
                        Quantity: item?.quantity?.toString(),
                        'Price Per Unit': checkIsBuilderProduct(item, selectDataForPublishedProducts, locale)
                          ? ''
                          : `\t$${(item?.variant?.price?.centAmount / 100).toFixed(item?.variant?.price?.fractionDigits)}`,
                    };
                })),
            );
            downloadCSVFrontend(enhancedData, filename);

        } catch (error) {
            console.error('Download error:', error);
            setErrorMessage('Failed to download the file. Please try again later.');
        } finally {
            setLoading(false);
            setTimeout(() => setErrorMessage(''), 5000);

        }
    };

    const exportButtonData = [
        { label: 'Export to Excel', onClick: handleDownloadExcel },
        { label: 'Download CSV', onClick: handleDownloadCSV },
    ];

    return (
        <>
            <div className="flex flex-col items-end">
                <div className="flex items-center justify-end gap-[10px]">
                    {exportButtonData.map(({ label, onClick }) => (
                        <ExportButton
                            key={label}
                            label={label}
                            onClick={onClick}
                            disabled={!allProducts?.length}
                        />
                    ))}
                </div>
                {
                    errorMessage && <span className="text-red-500 text-14 font-medium mt-4">{errorMessage}</span>
                }
            </div>

            {loading && <><Spinner />
                    <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
            </>}
        </>
    );
};

export default ExportToExcelAndCsv;

export const formatAttributes = (item: any) => {
    const primary = [PRODUCT_TYPE, RANGE, COLOR, ITEM_MATERIAL, COATING_METHOD].map(attr => {
        const value = item?.variant?.attributes?.[attr];
        if (!value || value === 'Not Applicable') return null;

        return {
            name: attr.replaceAll('-', ' ').replace('gwa', ''),
            value: Array.isArray(value)
                ? value.join(', ')
                : typeof value === 'boolean'
                    ? value ? 'Yes' : 'No'
                    : value,
        };
    }).filter(Boolean);

    const additional = Object.entries(item.variant?.attributes ?? {})
        .filter(([key, value]) =>
            value !== '' &&
            value !== 'Not Applicable' &&
            value !== 0 &&
            !Array.isArray(value) &&
            key !== ITEM_NAME
        )
        .map(([key, value]) => {
            const displayValue = formatValueWithUnit(key, value);
            return {
                name: key.replaceAll('-', ' ').replace('gwa', ''),
                value: displayValue,
            };
        });

    return [...primary, ...additional];
};

const formatValueWithUnit = (key: string, value: any) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key === PAN_HEIGHT || key === OUTLET_LENGTH) return `${value} mm`;
    if (key === CAPACITY_SIZE) return `${value} L`;
    if (key === LITRES_PER_MINUTE) return `${value} L /min`;
    if (typeof value === 'string') return value.trim();
    return value;
};


type ExportButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
};

export const ExportButton: React.FC<ExportButtonProps> = ({ label, ...props }) => (
    <button
        type="button"
        className={`bg-white-100 disabled:cursor-default font-medium relative cursor-pointer overflow-hidden rounded-md border
         !border-[#A6C9CC] !bg-[#A6C9CC] px-[18px] py-[8px] text-[16px] font-semibold text-gray-700 transition hover:shadow-md 
         active:shadow-md active:outline-1 active:outline-2 
         active:outline-offset-0 active:outline-gray-300 disabled:bg-neutral-400 disabled:text-neutral-100`}
        {...props}
    >
        {label}
    </button>
);